import type { ApiResponse } from "../../../shared/types/api";
import { httpClient } from "../http-client";
import { NEW_API_ENDPOINTS } from "../../../shared/constants/api";
import type {
  OrderMailboxListResponse,
  CollectedEmailDetail,
  CollectedEmail,
  CreateOrderFromEmailRequest,
  EstimateItemInput,
} from "../../../domain/types/order-mailbox";

export class OrderMailboxApiService {
  /** 주문 메일 목록 (상태별 카운트 + 검색 + 페이지) */
  async list(
    companyId: number,
    status?: string,
    keyword?: string,
    page?: number,
    size?: number,
  ): Promise<ApiResponse<OrderMailboxListResponse>> {
    const response = await httpClient.get<
      ApiResponse<OrderMailboxListResponse>
    >(
      NEW_API_ENDPOINTS.orderMailboxController.GET.list(
        companyId,
        status,
        keyword,
        page,
        size,
      ),
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "주문 메일 목록을 가져오지 못했습니다.",
      );
    }

    return response.data;
  }

  /** 주문 메일 상세 */
  async detail(
    companyId: number,
    id: number,
  ): Promise<ApiResponse<CollectedEmailDetail>> {
    const response = await httpClient.get<ApiResponse<CollectedEmailDetail>>(
      NEW_API_ENDPOINTS.orderMailboxController.GET.detail(companyId, id),
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "주문 메일 상세를 가져오지 못했습니다.",
      );
    }

    return response.data;
  }

  /** 수동 새 메일 확인 (해당 회사 메일을 동기 수집) → 신규 수집 건수 */
  async refresh(companyId: number): Promise<ApiResponse<number>> {
    const response = await httpClient.post<ApiResponse<number>>(
      NEW_API_ENDPOINTS.orderMailboxController.POST.refresh(companyId),
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "메일 수집에 실패했습니다.",
      );
    }

    return response.data;
  }

  /** 수집 메일을 실제 주문으로 생성 */
  async createOrder(
    companyId: number,
    id: number,
    request: CreateOrderFromEmailRequest,
  ): Promise<ApiResponse<CollectedEmail>> {
    const response = await httpClient.post<ApiResponse<CollectedEmail>>(
      NEW_API_ENDPOINTS.orderMailboxController.POST.createOrder(companyId, id),
      request,
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "주문 생성에 실패했습니다.",
      );
    }

    return response.data;
  }

  /** 수집 메일에 고객(유저) 연결 */
  async matchCustomer(
    companyId: number,
    id: number,
    userId: number,
  ): Promise<ApiResponse<CollectedEmail>> {
    const response = await httpClient.post<ApiResponse<CollectedEmail>>(
      NEW_API_ENDPOINTS.orderMailboxController.POST.matchCustomer(companyId, id),
      { userId },
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "고객 연결에 실패했습니다.",
      );
    }

    return response.data;
  }

  /** 견적서 PDF 다운로드 (blob 응답) */
  async downloadEstimate(
    companyId: number,
    id: number,
    items: EstimateItemInput[],
  ): Promise<{ blob: Blob; fileName: string | null }> {
    const response = await httpClient.post<Blob>(
      NEW_API_ENDPOINTS.orderMailboxController.POST.downloadEstimate(
        companyId,
        id,
      ),
      { items },
      { responseType: "blob" },
    );

    return {
      blob: response.data,
      fileName: extractFileName(response.headers?.["content-disposition"]),
    };
  }

  /** 견적서 메일 전송 → 실제 발송된 수신자 이메일 */
  async sendEstimate(
    companyId: number,
    id: number,
    items: EstimateItemInput[],
    toEmail?: string | null,
  ): Promise<ApiResponse<string>> {
    const response = await httpClient.post<ApiResponse<string>>(
      NEW_API_ENDPOINTS.orderMailboxController.POST.sendEstimate(companyId, id),
      { items, toEmail: toEmail || null },
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "견적서 메일 전송에 실패했습니다.",
      );
    }

    return response.data;
  }
}

/** Content-Disposition 헤더에서 파일명 추출 (RFC 5987 filename* 우선) */
function extractFileName(contentDisposition?: string): string | null {
  if (!contentDisposition) return null;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      /* fall through */
    }
  }

  const plainMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return plainMatch ? plainMatch[1] : null;
}

export const orderMailboxApiService = new OrderMailboxApiService();
